import React from 'react';
import { CheckCircle, TrendingUp, Shield, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../mock/data';
import SEO from '../components/SEO';

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Our Services | Stock Advisory Packages | Equi Pulse Research"
        description="Explore Equi Pulse Research advisory services - EQUI RIDER, EQUI HUNTER & EQUI MASTER packages offering swing trades, intraday calls, derivatives trading tips & portfolio guidance."
        keywords="stock advisory services, trading packages, swing trade tips, intraday trading calls, derivatives advisory, SEBI registered advisor"
        url="/services"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Stock Advisory <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-xl text-gray-300">
              SEBI registered research analyst offering expert financial insights and trading strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative">
                    {service.popular && (
                      <div className="inline-block mb-4 px-6 py-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold rounded-full shadow-lg">
                        Most Popular
                      </div>
                    )}
                    <h2 className="text-4xl font-bold text-white mb-4">
                      <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
                        {service.name}
                      </span>
                    </h2>
                    <p className="text-gray-300 text-lg mb-8">{service.tagline}</p>

                    {/* Features */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-amber-400 mb-4">What You Get</h3>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Why Choose */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-amber-400 mb-4">Why Choose {service.name}</h3>
                      <ul className="space-y-3">
                        {service.whyChoose.map((reason, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <TrendingUp className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                            <span className="text-gray-300">{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to="/contact"
                      className="inline-block px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300"
                    >
                      Join {service.name} Today!
                    </Link>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-emerald-700/30">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/20 to-transparent rounded-bl-full" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-amber-500/20 to-transparent rounded-tr-full" />
                    
                    <div className="relative space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-slate-950/50 rounded-lg border border-emerald-700/20">
                        <TrendingUp className="w-8 h-8 text-emerald-500" aria-hidden="true" />
                        <div>
                          <p className="text-white font-semibold">Real-Time Analysis</p>
                          <p className="text-gray-400 text-sm">Live market insights</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-4 bg-slate-950/50 rounded-lg border border-amber-700/20">
                        <Shield className="w-8 h-8 text-amber-500" aria-hidden="true" />
                        <div>
                          <p className="text-white font-semibold">Risk Management</p>
                          <p className="text-gray-400 text-sm">Stop-loss & targets</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-4 bg-slate-950/50 rounded-lg border border-emerald-700/20">
                        <BarChart3 className="w-8 h-8 text-emerald-500" aria-hidden="true" />
                        <div>
                          <p className="text-white font-semibold">Expert Research</p>
                          <p className="text-gray-400 text-sm">Data-driven strategies</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-950 via-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Not Sure Which Service{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
                Is Right for You?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Book a free consultation with our experts to find the perfect plan for your investment goals.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-2xl shadow-emerald-900/50 transition-all duration-300"
            >
              Book Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
